import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import { each, find, reduce, some, indexOf } from 'lodash';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import ProgressBar from '../progress-bar';
// Import Actions
import { fetchAllFilters } from '../../modules/Setup/FilterActions';
import { getFilters } from '../../modules/Setup/FilterReducer';
import CRMInstanceName from '../crm-instance-name';

// Import Selectors
import { getUser, getCanSetupOrganization, getUserCurrentOrganization } from '../../modules/Auth/AuthReducer';
import ManagerList from './managers';

export default (ComposedComponent, checkMappings = [], skipTitle = false) => {
  const initValues = () => reduce(checkMappings, (items, checkMapping) => {
    items[checkMapping] = false;
    return items;
  }, {});

  class RequiredSetupStages extends Component {
    constructor() {
      super();
      this.state = {
        loading: false,
        currentOrgId: null,
        error: null,
        setups: initValues(),
      };
    }

    componentDidMount() {
      this.checkProps(this.props);
    }

    componentWillReceiveProps(nextProps) {
      this.checkProps(nextProps);
    }

    checkProps(props) {
      const { user, userCurrentOrganization } = props;
      if (user._id) {
        if (!userCurrentOrganization) {
          this.setState({ currentOrgId: null, setups: initValues() });
          return;
        }
        const organizationId = userCurrentOrganization._id;
        if (this.state.currentOrgId !== organizationId) {
          this.setState({ loading: true, error: null, currentOrgId: organizationId });
          this.props.dispatch(fetchAllFilters()).then(
            (res) => {
              const { filters } = res;
              const setups = {};
              each(checkMappings, (checkMapping) => {
                setups[checkMapping] = false;
                const ft = find(filters, { tag: checkMapping });
                if (ft) {
                  const { mappings } = ft;
                  setups[checkMapping] = some(mappings, mapping => (mapping.values && mapping.values.length > 0));
                }
              });
              this.setState({ loading: false, error: null, setups });
            },
            (error) => {
              this.setState({ loading: false, error });
            },
          );
        }
      }
    }

    renderError() {
      const { error } = this.state;
      if (!error) {
        return null;
      }
      let message = error.messge;
      if (!message && error && error.response && error.response.data) {
        message = error.response.data.error || error.response.data;
      }
      if (!message && error && error.data && error.data.message) {
        ({ message } = error.data);
      }
      return (
        <div className="container">
          <p />
          <div className="alert alert-danger"><Fa name="exclamation-triangle" /> {message}</div>
        </div>
      );
    }

    renderSetupStageHint() {
      const { userCurrentOrganization, canSetupOrganization } = this.props;
      const emailSupport = process.env.REACT_APP_EMAIL_SUPPORT;
      return (
        <div className="container">
          {!skipTitle && <Helmet title="Congratulations" />}
          {canSetupOrganization &&
            <div>
              {!skipTitle && <h1 className="page-header">Congratulations!</h1>}
              <div className="well">
                <p>
                  You have completed the <CRMInstanceName /> data sync step required for TruVoice Automation&trade;.
                  <br />
                  You just need to complete a few more steps to have TruVoice Automation&trade; up and running for <strong>{userCurrentOrganization.display_name}</strong>.
                </p>
                <p>Required setup steps:</p>
                <ul>
                  <li><Link to="/setup/filters/stage">Map your <CRMInstanceName /> opportunity stages</Link> to the appropriate outcomes (win, loss, or no decision).</li>
                </ul>
                <p>
                  You can add greater data analysis capabilities for you and your users in TruVoice Automation&trade; by completing the following optional steps
                  (each of these <Link to="/setup">setup areas</Link> can be reached by clicking on your name
                  in the toolbar and clicking on the <strong>My organization</strong> option):
                </p>
                <ul>
                  <li>
                    <Link to="/setup/filters/dealType">Map your opportunity types</Link> to the deal types defined in TruVoice Automation&trade; (renewals, new business, add-on, and upsell)
                  </li>
                  <li>
                    <Link to="/setup/filters/repType">Create sales representative types and map the sales reps</Link> for your organization to those types
                  </li>
                  <li>
                    <Link to="/setup/filters/repRegion">Create sales regions and map the sales reps</Link> that belong to those regions allowing you to analyze your data by region
                  </li>
                  <li>
                    <Link to="/setup/amount">Change the way TruVoice Automation calculates opportunity amounts</Link>
                    &nbsp;(default is to use the Opportunity <code>amount</code> field in <CRMInstanceName />, but you can change this to use a different field, or a combination of fields)
                  </li>
                  <li>
                    <Link to="/setup/products">Create product families based on the way you define your products</Link>
                    &nbsp;(using the <CRMInstanceName /> <code>Product2</code> object or a custom Opportunity field)
                  </li>
                  <li>
                    <Link to="/setup/filters">Create custom filters</Link>
                    &nbsp;based on any field on your <code>Opportunity</code>, <code>Account</code>, <code>User</code>, <code>Product</code>,
                    or <code>OpportunityCompetitor</code> <CRMInstanceName /> objects
                  </li>
                  <li>
                    <Link to="/setup/organization">Set TruVoice Automation&rsquo; data sync time and frequency</Link>
                  </li>
                  <li>
                    <Link to="/setup/users">Add both <CRMInstanceName /> users and non-<CRMInstanceName /> users to TruVoice Automation</Link>
                  </li>
                </ul>
                {emailSupport !== undefined &&
                  <p>
                    For further information or if you have any questions please do not hesitate to <a href={`mailto:${emailSupport}`}>contact the support team</a>.
                  </p>
                }
              </div>
            </div>
          }
          {!canSetupOrganization &&
            <div>
              {!skipTitle && <h1 className="page-header">Congratulations!</h1>}
              <div className="well">
                <p>
                  You have completed the <CRMInstanceName /> data sync step required for TruVoice Automation&trade;.
                </p>
                <p>
                  Please note that <strong>{userCurrentOrganization.display_name}</strong> has not been completely set up yet.
                  There is still one more step that needs to be completed in order for you to use TruVoice Automation&trade;.
                  Contact a TruVoice Automation&trade; manager for your organization to have them complete the setup process.
                </p>
                <ManagerList />
                {emailSupport !== undefined &&
                  <p>
                    For further information or if you have any questions please do not hesitate to <a href={`mailto:${emailSupport}`}>contact the support team</a>.
                  </p>
                }
              </div>
            </div>
          }
        </div>
      );
    }

    renderSetupDealTypeHint() {
      const { canSetupOrganization } = this.props;
      const emailSupport = process.env.REACT_APP_EMAIL_SUPPORT;
      return (
        <div className="container">
          {!skipTitle && <Helmet title="Congratulations" />}
          {!skipTitle && <h1 className="page-header">Congratulations!</h1>}
          {canSetupOrganization &&
            <div>
              {!skipTitle && <p>Initial data sync has completed and TruVoice Automation is ready to be configured.</p>}
              <div className="well">
                <p>Required setup steps:</p>
                <ul>
                  <li><Link to="/setup/filters/dealType">Setup deal type mappings</Link></li>
                </ul>
                {emailSupport !== undefined &&
                  <p>
                    For further information or if you have any questions please do not hesitate to <a href={`mailto:${emailSupport}`}>contact the support team</a>.
                  </p>
                }
              </div>
            </div>
          }
          {!canSetupOrganization &&
            <div className="well">
              <p>You need a manager to setup deal type mappings for your organization.</p>
              <ManagerList />
              {emailSupport !== undefined &&
                <p>
                  For further information or if you have any questions please do not hesitate to <a href={`mailto:${emailSupport}`}>contact the support team</a>.
                </p>
              }
            </div>
          }
        </div>
      );
    }

    renderSetupProductHint() {
      const { userCurrentOrganization, canSetupOrganization } = this.props;
      const emailSupport = process.env.REACT_APP_EMAIL_SUPPORT;
      return (
        <div className="container">
          {!skipTitle && <Helmet title="Congratulations" />}
          {!skipTitle && <h1 className="page-header">Congratulations!</h1>}
          {canSetupOrganization &&
            <div>
              {!skipTitle && <p>Initial data sync has completed and TruVoice Automation is ready to be configured.</p>}
              <div className="well">
                <p>Required setup steps:</p>
                <ul>
                  <li><Link to="/setup/filters/product">Setup product mappings</Link></li>
                </ul>
                {emailSupport !== undefined &&
                  <p>
                    For further information or if you have any questions please do not hesitate to <a href={`mailto:${emailSupport}`}>contact the support team</a>.
                  </p>
                }
              </div>
            </div>
          }
          {!canSetupOrganization &&
            <div className="well">
              <p>
                Please note that there is no products found for <strong>{userCurrentOrganization.display_name}</strong>.
                There is still one more step that needs to be completed in order for you to use TruVoice Automation&trade;.
                Contact a TruVoice Automation&trade; manager for your organization to have them complete the setup process.
              </p>
              <ManagerList />
              {emailSupport !== undefined &&
                <p>
                  For further information or if you have any questions please do not hesitate to <a href={`mailto:${emailSupport}`}>contact the support team</a>.
                </p>
              }
            </div>
          }
        </div>
      );
    }

    renderLoading() {
      const { loading } = this.state;
      if (loading) {
        return (
          <div className="container">
            <p />
            <ProgressBar />
          </div>
        );
      }
      return null;
    }

    render() {
      const { loading, error, setups } = this.state;
      const { filters } = this.props;
      let setup = true;
      each(setups, (s) => {
        if (!s) {
          setup = s;
        }
      });
      if (loading) {
        return this.renderLoading();
      } 
      if (error) {
        return this.renderError();
      } 
      if (!setup) {
        if (!setups.stage && indexOf(checkMappings, 'stage') >= 0) {
          return this.renderSetupStageHint();
        }
        if (!setups.dealType && indexOf(checkMappings, 'dealType') >= 0) {
          return this.renderSetupDealTypeHint();
        }
        if (!setups.product && indexOf(checkMappings, 'product') >= 0) {
          return this.renderSetupProductHint();
        }
        return null;
      }
      const p = reduce(checkMappings, (items, checkMapping) => {
        const ft = find(filters, { tag: checkMapping });
        if (ft) {
          items[`${checkMapping}Mappings`] = ft.mappings;
        }
        return items;
      }, {});
      return <ComposedComponent {... p} {... this.props} />;
    }
  }


  // Retrieve data from store as props
  function mapStateToProps(state) {
    return {
      user: getUser(state),
      filters: getFilters(state),
      canSetupOrganization: getCanSetupOrganization(state),
      userCurrentOrganization: getUserCurrentOrganization(state),
    };
  }

  RequiredSetupStages.propTypes = {
    dispatch: PropTypes.func.isRequired,
    user: PropTypes.shape({}).isRequired,
    canSetupOrganization: PropTypes.bool.isRequired,
    userCurrentOrganization: PropTypes.shape({}).isRequired,
    filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  };

  return connect(mapStateToProps)(RequiredSetupStages);
}
